/**
 * useAutoGeocoding - Hook para geocodificación automática de infraestructuras
 * 
 * ARCH-04 FIX: Procesa TODAS las infraestructuras para obtener coordenadas
 * de referencia y validación cruzada contra fuentes oficiales.
 * 
 * Las coordenadas del documento son un DATO a validar, no verdad absoluta.
 * El DocumentVsReferenceComparator compara doc vs referencia (Casos A/B/C).
 * 
 * Se ejecuta automáticamente después de la normalización en Step1.
 * 
 * @module hooks/useAutoGeocoding
 * @version 2.0.0 - ARCH-04: Validación cruzada obligatoria
 */

import { useState, useCallback, useRef } from 'react'
import { GeocodingOrchestrator, type OrchestrationOptions } from '../services/geocoding'
import { compareCoordinates } from '../services/validation/DocumentVsReferenceComparator'
import {
  getDERAG10R2Service,
  claseHidraulicaG10,
  type DERAG10R2Service,
  type TestigoG10,
} from '../services/geocoding/specialized/DERAG10R2Service'
import { getDiscrepancyThreshold } from '../lib/crossValidation/clusters'
import { getCodigoINE } from '../lib/codigosINEUnificado'
import { runWithConcurrency } from '../services/performance/BatchProfiler'
import {
  deriveProcedencia,
  emptyViaBreakdown,
  esCentroideMunicipal,
  type ViaBreakdown,
} from '../utils/procedenciaVia'
import { loadLocalData } from '../lib/LocalDataService'
import { precargarContorno } from '../services/boundaries/MunicipalBoundaryService'
import type { ProcessedInfrastructure } from '../types/processing'
import type { DocumentMetadata } from '../types/processing'
import type { GeolocalizacionParcial } from '../utils/pickInfrasForDisplay'

// ============================================================================
// TIPOS
// ============================================================================

export interface AutoGeocodingProgress {
  /** Infraestructuras procesadas */
  processed: number
  /** Total a procesar */
  total: number
  /** Porcentaje completado */
  percentage: number
  /** Infraestructura actual (lo lee `shared/GeocodingProgressBar`, Step1 legado) */
  current?: string
  /**
   * SERP1 (DOP-4 a″): el mismo nombre con el campo que espera
   * `NormalizationProgress` de `wizard-v2/step1-normaliza/ProgressBar`, al que
   * `WizardPageV2` pasa este progreso tal cual (`geoProgress`). Sin él la línea
   * «↳ fila» nunca se pintaba en geocodificación (OBS-UI-GEOPROGRESS-CURRENT-TYPING).
   */
  currentInfraName?: string
  /**
   * P1-S4 (§C, letra c reinterpretada): contador acumulado por vía de
   * resolución durante la pasada de geocodificación. La cascada es
   * per-ítem (no per-fase secuencial), por lo que la «fase por vía» se
   * expone como acumulado en runtime, no como fases consecutivas.
   */
  porVia?: ViaBreakdown
  /**
   * P1UNO (DOP-0 a′): contador acumulado por tramo de calidad durante la
   * pasada, con la misma regla que `computeDocumentStatus` (eje primario:
   * coordenadas finitas; los tramos de puntuación solo con coordenadas).
   * Sembrado al arrancar con las filas que NO entran en la pasada (su
   * estado no cambia), de modo que la barra en vivo y la final cuadran.
   */
  porCalidad?: CalidadBreakdown
}

/** P1UNO: tramos de calidad del panel de P1 (mismos nombres que DocumentStatus) */
export interface CalidadBreakdown {
  /** score ≥90 con coordenadas */
  high: number
  /** score 70-89 con coordenadas */
  medium: number
  /** score 50-69 con coordenadas */
  low: number
  /** score <50 o null con coordenadas */
  lowScore: number
  /** sin X/Y finitos */
  withoutCoords: number
}

export function emptyCalidadBreakdown(): CalidadBreakdown {
  return { high: 0, medium: 0, low: 0, lowScore: 0, withoutCoords: 0 }
}

/** Tramo de calidad de una infraestructura (regla de `computeDocumentStatus`) */
export function tramoCalidad(infra: ProcessedInfrastructure): keyof CalidadBreakdown {
  if (!Number.isFinite(infra.xFinal) || !Number.isFinite(infra.yFinal)) return 'withoutCoords'
  const score = infra.score
  if (score == null || score < 50) return 'lowScore'
  if (score >= 90) return 'high'
  if (score >= 70) return 'medium'
  return 'low'
}

export interface AutoGeocodingStats {
  /** Total de infraestructuras a geocodificar */
  total: number
  /** Geocodificaciones exitosas */
  success: number
  /** Geocodificaciones fallidas */
  failed: number
  /** Geocodificadores usados (desglose) */
  geocoderUsage: Record<string, number>
  /** Score promedio de geocodificación */
  averageScore: number
  /** Tiempo total en ms */
  processingTime: number
  /** ARCH-04: Estadísticas de validación cruzada */
  crossValidation?: {
    validados: number      // Caso A.1: <25m
    revisionMenor: number  // Caso A.2: 25-100m
    corregidos: number     // Caso A.3: usar referencia
    conflictos: number     // Caso A.4: >500m
    geocodificados: number // Caso B: sin coords doc
    sinCoordenadas: number // Caso C: sin ninguna
  }
}

export interface UseAutoGeocodingResult {
  /** Ejecutar geocodificación en infraestructuras que lo necesiten */
  geocodeNeeded: (
    infrastructures: ProcessedInfrastructure[],
    metadata: DocumentMetadata
  ) => Promise<ProcessedInfrastructure[]>
  /** Estado de procesamiento */
  isGeocoding: boolean
  /** Progreso actual */
  progress: AutoGeocodingProgress
  /** Resultados procesados (solo al terminar la tanda: «no vacío» = terminó) */
  results: ProcessedInfrastructure[]
  /**
   * P1PROV-a: lo hecho mientras la tanda corre (filas terminadas por id y filas
   * de la pasada). `null` fuera de una tanda vigente. Solo para la tabla de P1.
   */
  parcial: GeolocalizacionParcial | null
  /** Estadísticas de geocodificación */
  stats: AutoGeocodingStats | null
  /** Error si ocurrió */
  error: string | null
  /** Resetear estado */
  reset: () => void
  /**
   * P2-S3: actualiza campos de un resultado por id sin mutar los demás
   * (clon del patchResult de useNormalization — necesario para que
   * «Confirmar elemento» alcance la fuente geocodificada del display).
   */
  patchResult: (id: string, patch: Partial<ProcessedInfrastructure>) => void
}

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Concurrencia para APIs externas (CartoCiudad, CDAU, Nominatim).
 * 2 peticiones simultáneas: respeta rate-limits y evita sobrecarga.
 * R.03/Web-Workers: incrementar a 4-6 cuando se paralelice en Workers.
 */
const EXTERNAL_CONCURRENCY = 2

/**
 * Concurrencia para datos locales R2/DERA (sin red externa).
 * 8 peticiones simultáneas: el Worker de Cloudflare aguanta sin problema.
 */
const R2_CONCURRENCY = 8  // eslint-disable-line @typescript-eslint/no-unused-vars

/**
 * C4 — InputQualityFilter: patrones de metadatos que NO son infraestructuras.
 * Ejemplos: "AFORO: 130", "CAPACIDAD: 20", "ENTRADA: 2", "TELEFONO: 958…"
 * Registros con nombre que coincida con estos patrones no gastan peticiones a
 * APIs externas (FILC4: salen «sin contrastar», ver `marcarSinContrastar`).
 *
 * Patrón compuesto:
 *   – Grupo 1: palabras clave seguidas de «:» o de un número (metadatos del doc:
 *     «AFORO: 130», «PLANTA 2»)
 *   – Alternativa 2: números puros de 4-5 dígitos (aforos, capacidades…)
 *   – Alternativa 3: siglas puras de 1-4 letras con dígitos opcionales (ABC, E01…)
 *
 * FILC4 (DOP-2 a, 1-nov-2026): la palabra clave ya no basta con un espacio
 * detrás. Con «[\s:]» apartaba infraestructuras reales que empiezan por esas
 * palabras («Nave Municipal», «Zona Deportiva y Piscina», «Planta de
 * transferencia RSU»: 14 filas del corpus) y no acertaba en ningún metadato
 * (0 de 2 135 filas con «:» o número detrás). Las siglas siguen fuera (DOP-3 b).
 */
const INPUT_QUALITY_FILTER_PATTERNS =
  /^(AFORO|CAPACIDAD|ENTRADA|AFORO\s+TOTAL|AFORO\s+M[AÁ]XIMO|COBERTURA|TEL[EÉ]FONO|FAX|EMAIL|CORREO|NIF|CIF|CP|C[OÓ]DIGO\s+POSTAL|PLANTA|M[OÓ]DULO|SECTOR|ZONA|BLOQUE|NAVE)(\s*:|\s+\d)|^\d{4,5}$|^[A-Z]{1,4}\d*$/i

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Evalúa la calidad léxica de un nombre de infraestructura.
 * Devuelve un score 0-100 para filtrar entradas que no son geocodificables:
 *   0  — vacío, muy corto o número puro
 *   10 — sigla pura 1-4 letras (CEIP, IES, ELA…)
 *   30 — palabra única corta (< 6 chars) sin dígitos
 *  100 — nombre con varias palabras o longitud suficiente
 *
 * @internal Exportada en `_internal` para pruebas unitarias
 */
function scoreNameQuality(nombre: string): number {
  const s = nombre.trim()
  if (s.length < 4) return 0
  if (/^\d+$/.test(s)) return 0
  if (/^[A-Z]{1,4}\d*$/i.test(s)) return 10
  if (s.split(/\s+/).length === 1 && s.length < 6) return 30
  return 100
}

/**
 * Determina si una infraestructura necesita geocodificación.
 *
 * ARCH-04 FIX: TODAS las infraestructuras deben pasar por geocodificación
 * para obtener coordenadas de referencia y validación cruzada.
 *
 * C4 — InputQualityFilter: registros cuyo nombre coincida con
 * INPUT_QUALITY_FILTER_PATTERNS son metadatos del documento (AFORO, CAPACIDAD…),
 * no infraestructuras geocodificables. Se marcan como SKIP_METADATA y se
 * devuelven intactos para no gastar peticiones a APIs externas.
 */
function needsGeocoding(infra: ProcessedInfrastructure): boolean {
  // F307 (INFADM-S2): las plantillas «pendiente de rellenar» se conservan como
  // elemento (marcadas) pero NO se geocodifican — evita que «_____Indicar_____»
  // consuma la cascada hasta timeout.
  if (infra.esPlaceholder) return false
  const nombre = (infra.nombre ?? '').trim()
  if (INPUT_QUALITY_FILTER_PATTERNS.test(nombre)) {
    return false   // SKIP_METADATA: metadato del documento, no infraestructura
  }
  if (scoreNameQuality(nombre) < 20) {
    return false   // SKIP_LOW_QUALITY: nombre demasiado corto, sigla o numérico
  }
  // ARCH-04: Siempre geocodificar para validación cruzada
  return true
}

/** FILC4: fuente de la fila que no entra en la pasada y conserva su coordenada. */
export const SOURCE_SIN_CONTRASTAR = 'original:sin_contrastar'

/** FILC4: motivo visible de por qué esa fila no se contrastó. */
export const MOTIVO_SIN_CONTRASTAR =
  'No se consultó ninguna fuente: el nombre parece una sigla o un dato del documento, no una instalación'

/**
 * FILC4 (DOP-1 c, 1-nov-2026): la fila que `needsGeocoding` deja fuera de la
 * pasada no se contrasta con ninguna fuente, pero salía con la nota de LECTURA
 * de su coordenada (95, la de `normalizeInfrastructure`), que en P1 se lee
 * como «contrastada por varias fuentes». Se le aplica la regla que ya rige para
 * un documento con coordenada y sin referencia (caso A.1 del comparador: 70,
 * `REVISION_MENOR`, coordenada del documento intacta), con fuente y motivo
 * propios. La coordenada que se conserva es la normalizada (`xFinal`/`yFinal`),
 * la misma que la tabla ya enseñaba. Sin coordenada final se devuelve tal cual
 * (ya sale con 0 / `pending`).
 *
 * @internal Exportada en `_internal` para pruebas unitarias
 */
function marcarSinContrastar(
  infra: ProcessedInfrastructure,
  metadata: DocumentMetadata
): ProcessedInfrastructure {
  const x = infra.xFinal
  const y = infra.yFinal
  if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) {
    return infra
  }
  const comparison = compareCoordinates({
    id: infra.id,
    nombre: infra.nombre || '',
    documento: { x, y, crs: 'EPSG:25830' },
    referencia: null,
    codigoINE: metadata.ineCode || infra.municipio || ''
  })
  return {
    ...infra,
    score: comparison.score,
    confidence: comparison.score >= 85 ? 'ALTA' : comparison.score >= 70 ? 'MEDIA' : 'BAJA',
    source: SOURCE_SIN_CONTRASTAR,
    crossValidation: {
      caso: comparison.caso,
      flag: comparison.flag,
      distanciaMetros: comparison.distanciaMetros,
      fuenteCoordenadas: comparison.fuenteCoordenadas,
      sourcesCount: 0,
      requiresManualReview: false,
      reviewReason: MOTIVO_SIN_CONTRASTAR,
    }
  }
}

/**
 * Convierte infraestructura a opciones de geocodificación
 */
function toOrchestrationOptions(
  infra: ProcessedInfrastructure,
  metadata: DocumentMetadata
): OrchestrationOptions {
  // FIX BUG-GEO-001: Cascada de fallbacks para ineCode
  // Evita que codMun sea undefined cuando metadata.ineCode no está disponible
  const codMunResolved = metadata.ineCode 
    || (infra.municipio ? getCodigoINE(infra.municipio) : null)
    || (metadata.municipality ? getCodigoINE(metadata.municipality) : null)
    || undefined;
    
  return {
    name: infra.nombre || '',
    municipality: infra.municipio || metadata.municipality || '',
    province: metadata.province || 'Andalucía',
    codMun: codMunResolved,
    address: infra.direccion || infra.direccionAlt || undefined,
    declaredType: infra.tipo || undefined, // FIX-GEO-002: Pasar tipología del documento
    useLocalData: true,      // L0: DERA offline
    useGenericFallback: true, // L5-L7: CDAU, CartoCiudad, Nominatim
    useNGA: true,            // L2: Nomenclátor topónimos
    useIAID: true,           // L3: Deportes
    useOverpass: true,       // L4: OSM
    crossValidate: true,     // F4.06: Habilitar validación cruzada multi-fuente
    timeout: 10000,          // 10s por infraestructura
    // OBS-GEO-ADDRESSTYPE-NO-FORWARD (06-jun-2026): reenviar addressType para que
    // el orquestador active el atajo IEP-06 (cadastral/toponym/road/sports). Sin esto
    // el atajo muere en P1 aunque el parser lo haya clasificado correctamente.
    addressType: infra.addressType,
    // CATPP-BC (parte C): reenviar polígono/parcela catastrales (IEP-08) para que
    // el orquestador los pase a CatastroGeocoderV2 (estrategia polygon_parcel).
    // Sin esto, las rústicas «Pol X, Parc Y» mueren en el centroide urbano.
    poligonoCatastral: infra.poligonoCatastral,
    parcelaCatastral: infra.parcelaCatastral,
    // CENTOR (12-sep-2026): el orquestador no fabrica centroide municipal si el
    // documento ya trae X/Y (misma condición que `docX`/`docY` en applyGeocodingResult).
    hasDocumentCoords: tieneCoordenadaDocumento(infra),
  }
}

/** CENTOR: ¿trae el documento una coordenada X/Y numérica para este elemento? */
function tieneCoordenadaDocumento(infra: ProcessedInfrastructure): boolean {
  if (!infra.hasCoordinates || !infra.xOriginal || !infra.yOriginal) return false
  return !isNaN(parseFloat(String(infra.xOriginal))) && !isNaN(parseFloat(String(infra.yOriginal)))
}

// CTRN-G (13-sep-2026): `esCentroideMunicipal` vive en `utils/procedenciaVia.ts`
// (la consulta también el orquestador); aquí solo se importa y se reexpone en `_internal`.

/** EDAR-b: el testigo G10 solo confirma el plan si está a esta distancia o menos (m). */
export const TESTIGO_G10_CONFIRMA_M = 100
/** EDAR-b: nota de una fila con coordenada del plan sin contrastar (caso A.1 o sin respaldo). */
const NOTA_SIN_CONTRASTAR = 70

/**
 * EDAR-b (DOP-1 c, DOP-3 b; Luis 1-nov-2026): DERA G10 es **testigo** de las depuradoras,
 * potabilizadoras y depósitos: nunca gana en el orquestador ni entra en la validación cruzada;
 * solo puede subir a 80/100 una fila que hoy queda en 70 («sin contrastar») cuando su
 * instalación está a ≤ 100 m del plan. No mueve la coordenada (a ≤ 100 m el comparador conserva
 * la del documento), no baja una nota ni quita un 30. Medido en la tarea EDAR y en EDAR-b
 * (`.ptel/p0_edarb_20261101/`): en el corpus suben Los Gallardos #48, Benalúa #44 y Benizalón
 * #12; los puntos de G10 que fallan (Almanzora a 535 m, La Hoica a 6,4 km) quedan lejos y no
 * cuentan. Sustituye a la guarda de EDAR-a, que se retiró por no tener consumidor (G10 no
 * llega a las filas hidráulicas por el orquestador).
 *
 * @internal Exportada en `_internal` para pruebas unitarias
 */
function confirmaConTestigoG10(
  comparison: ReturnType<typeof compareCoordinates>,
  testigo: TestigoG10 | null | undefined,
  docX: number | null,
  docY: number | null,
  base: { id: string; nombre: string; municipio: string; codigoINE: string }
): { comparison: ReturnType<typeof compareCoordinates>; testigoG10: NonNullable<ProcessedInfrastructure['crossValidation']>['testigoG10'] } | null {
  if (!testigo || docX === null || docY === null) return null
  if (comparison.score !== NOTA_SIN_CONTRASTAR || comparison.fuenteCoordenadas !== 'documento') return null
  const distancia = Math.hypot(testigo.x - docX, testigo.y - docY)
  if (distancia > TESTIGO_G10_CONFIRMA_M) return null
  const conTestigo = compareCoordinates({
    ...base,
    documento: { x: docX, y: docY, crs: 'EPSG:25830' },
    referencia: {
      x: testigo.x,
      y: testigo.y,
      fuente: 'DERA',
      confianza: 100,
      nombreOficial: testigo.nombre,
    },
  })
  if (conTestigo.score <= comparison.score || conTestigo.fuenteCoordenadas !== 'documento') return null
  return {
    comparison: conTestigo,
    testigoG10: {
      id: testigo.id,
      nombre: testigo.nombre,
      distanciaMetros: Math.round(distancia * 10) / 10,
      modo: testigo.modo,
    },
  }
}

/**
 * Actualiza infraestructura con resultado de geocodificación
 * 
 * ARCH-04 FIX: Integra validación cruzada con DocumentVsReferenceComparator
 * para comparar coordenadas del documento vs referencia oficial.
 * 
 * Casos del flujo PTEL v14:
 * - CASO A: Doc tiene coords → comparar con referencia
 *   - A.1: distancia ≤25m → VALIDADO (score 100)
 *   - A.2: 25m < dist ≤100m → REVISION_MENOR (score 80)
 *   - A.3: 100m < dist ≤500m → USAR_REFERENCIA (score 60)
 *   - A.4: dist >500m → REVISION_MANUAL (score 30)
 * - CASO B: Doc NO tiene coords → usar referencia geocodificada
 * - CASO C: Sin coords en doc ni referencia → pendiente manual
 *
 * CENT60 (12-sep-2026): un centroide municipal NO es una referencia contra la que
 * validar. Si el documento trae coordenada y el orquestador solo pudo dar el
 * centroide, se pasa `referencia: null` → caso A.1 (70, «sin validar», coordenada
 * del documento intacta). Antes caía en A.3 con 60 y la coordenada real quedaba
 * pisada por el centroide (medido `p0_cent60_20260912`: 6 de 11 en el corpus).
 * Sin coordenada del documento (caso B) el centroide se conserva con su
 * `confidence` y `source` lleva el sufijo `:centroide` para que la UI lo distinga.
 */
function applyGeocodingResult(
  infra: ProcessedInfrastructure,
  geocoding: Awaited<ReturnType<GeocodingOrchestrator['geocode']>>,
  metadata: DocumentMetadata,
  /** EDAR-b: testigo G10 de una fila hidráulica (solo confirma; ver `confirmaConTestigoG10`). */
  testigo: TestigoG10 | null = null
): ProcessedInfrastructure {
  // Coordenadas del documento (si existen)
  const docX = infra.hasCoordinates && infra.xOriginal 
    ? parseFloat(String(infra.xOriginal)) 
    : null
  const docY = infra.hasCoordinates && infra.yOriginal 
    ? parseFloat(String(infra.yOriginal)) 
    : null
  
  // Validar que docX/docY son números válidos
  const validDocX = docX !== null && !isNaN(docX) ? docX : null
  const validDocY = docY !== null && !isNaN(docY) ? docY : null

  // CENT60: el centroide municipal no valida ni corrige una coordenada del documento.
  const centroideMunicipal = esCentroideMunicipal(geocoding.geocoderUsed, geocoding.geocoding?.source)
  const centroideConDocCoords = centroideMunicipal && validDocX !== null && validDocY !== null
  // MAPLEY (DOP-3=a): `:centroide` se sella una sola vez. `specialized:universal:centroide`
  // y `specialized:social:centroide` ya terminan así; antes salía `…:centroide:centroide`.
  // Comparación estricta por sufijo (no `esOrigenCentroide`): con Fix-B
  // (`iner2:centroide-municipio`) el sufijo sigue haciendo falta para que la UI lo reconozca.
  const sourceCentroide = geocoding.geocoderUsed.endsWith(':centroide')
    ? `geocoded:${geocoding.geocoderUsed}`
    : `geocoded:${geocoding.geocoderUsed}:centroide`
  
  // PUNTOMEDIO-a («documento primero»): si la validación cruzada acabó con las fuentes en
  // disputa (radio del grupo > umbral de la tipología, el mismo criterio que `detectDiscrepancy`),
  // su coordenada es el punto medio, que no dio ninguna fuente: el documento se juzga contra
  // las fuentes, no contra ese punto, y no se corrige con él.
  const enDisputa = geocoding.geocoderUsed === 'cross_validated'
    && typeof geocoding.discrepancyMeters === 'number'
    && geocoding.discrepancyMeters > getDiscrepancyThreshold(geocoding.classification?.type)
  const fuentesEnDisputa = enDisputa
    ? (geocoding.sourcesConsulted ?? []).map(s => ({ x: s.x, y: s.y, fuente: s.source }))
    : null

  // PUNTOMEDIO-b (DOP-1 c, 2 a): las fuentes de la validación cruzada, con lo que encontró cada
  // una, para que el comparador decida si la referencia puede sustituir al documento (≥ 2
  // limpias). Sin desglose (cascada directa, Catastro o pedanía solos) la lista va vacía: no hay
  // respaldo y, entre 100 y 500 m, se conserva el documento.
  const fuentesRespaldo = (geocoding.crossValidationDetail?.sourceResults ?? []).map(s => ({
    x: s.x,
    y: s.y,
    fuente: String(s.source),
    nombreEncontrado: s.matchedName ?? null,
  }))

  // Usar DocumentVsReferenceComparator para validación cruzada
  const comparisonOrquestador = compareCoordinates({
    fuentesEnDisputa,
    fuentesRespaldo,
    municipio: infra.municipio || metadata.municipality || '',
    id: infra.id,
    nombre: infra.nombre || '',
    documento: {
      x: validDocX,
      y: validDocY,
      crs: 'EPSG:25830'
    },
    referencia: geocoding.geocoding && !centroideConDocCoords ? {
      x: geocoding.geocoding.x,
      y: geocoding.geocoding.y,
      fuente: geocoding.geocoderUsed as 'DERA' | 'CartoCiudad' | 'CDAU' | 'WFS' | 'Nominatim',
      confianza: geocoding.geocoding.confidence,
      nombreOficial: geocoding.geocoding.matchedName
    } : null,
    codigoINE: metadata.ineCode || infra.municipio || ''
  })

  // EDAR-b: el testigo G10 solo puede subir un 70 a 80/100 con el plan intacto.
  const confirmacionG10 = confirmaConTestigoG10(comparisonOrquestador, testigo, validDocX, validDocY, {
    id: infra.id,
    nombre: infra.nombre || '',
    municipio: infra.municipio || metadata.municipality || '',
    codigoINE: metadata.ineCode || infra.municipio || '',
  })
  const comparison = confirmacionG10?.comparison ?? comparisonOrquestador
  const rastroTestigo = confirmacionG10 ? { testigoG10: confirmacionG10.testigoG10 } : {}
  
  // Determinar coordenadas finales según comparación
  const finalCoords = comparison.coordenadasFinales
  const finalScore = comparison.score
  
  // Mapear score a confianza
  const scoreToConfidence = (s: number): 'CRITICA' | 'ALTA' | 'MEDIA' | 'BAJA' | 'NULA' => {
    if (s >= 85) return 'ALTA'
    if (s >= 70) return 'MEDIA'
    if (s >= 50) return 'BAJA'
    if (s >= 25) return 'CRITICA'
    return 'NULA'
  }
  
  // Determinar source y status según resultado de comparación
  let finalSource: string
  let finalStatus: ProcessedInfrastructure['status']
  
  switch (comparison.flag) {
    case 'VALIDADO':
      // Caso A.1: Coords doc confirmadas por referencia (<25m)
      finalSource = 'confirmed:document'
      finalStatus = 'original'
      break
    case 'REVISION_MENOR':
      // Caso A.2: Pequeña discrepancia (25-100m)
      finalSource = comparison.fuenteCoordenadas === 'documento' 
        ? 'original:minor_discrepancy' 
        : `corrected:${geocoding.geocoderUsed}`
      finalStatus = 'geocoded'
      break
    case 'USAR_REFERENCIA':
      // Caso A.3: Usar coords oficiales (100-500m discrepancia)
      finalSource = `corrected:${geocoding.geocoderUsed}`
      finalStatus = 'geocoded'
      break
    case 'REVISION_MANUAL':
      // Caso A.4: Discrepancia grave (>500m) - requiere revisión
      finalSource = 'conflict:manual_review'
      finalStatus = 'manual'
      break
    case 'GEOCODIFICADO':
      // Caso B: Doc sin coords, referencia exitosa
      // CENT60: si la «referencia» es el centroide municipal, se declara como tal.
      finalSource = centroideMunicipal
        ? sourceCentroide
        : `geocoded:${geocoding.geocoderUsed}`
      finalStatus = 'geocoded'
      break
    case 'BAJA_CONFIANZA':
      // Caso B: Doc sin coords, referencia dudosa
      finalSource = centroideMunicipal
        ? sourceCentroide
        : `geocoded:${geocoding.geocoderUsed}:low_confidence`
      finalStatus = 'geocoded'
      break
    case 'SIN_COORDENADAS':
    default:
      // Caso C: Sin coords en ninguna fuente
      finalSource = 'none'
      finalStatus = 'pending'
      break
  }
  
  // PAR1 · eslabón 2: la parcela que ganó la geocodificación viaja al elemento.
  // `toProcessedRecord` y `MatchingEngine` leen `infra.catastroParcel`; hasta
  // ahora sólo lo poblaba el enriquecimiento por vecino más próximo de
  // `useExportWizard`, que `CCB1` midió equivocado en 6 de 8. Ésta viene de una
  // atadura por refcat/dirección/nombre, no de una lotería de cercanía.
  // Spread condicional a propósito: `catastroParcel: undefined` pisaría el valor
  // que `...infra` ya trajera.
  const catastroParcel = geocoding.geocoding?.catastroParcel

  // Construir resultado final
  if (finalCoords) {
    return {
      ...infra,
      xFinal: finalCoords.x,
      yFinal: finalCoords.y,
      hasCoordinates: true,
      score: finalScore,
      confidence: scoreToConfidence(finalScore),
      status: finalStatus,
      source: finalSource,
      geocoding: geocoding,
      ...(catastroParcel ? { catastroParcel } : {}),
      // Metadatos de validación cruzada para debug
      crossValidation: {
        caso: comparison.caso,
        flag: comparison.flag,
        distanciaMetros: comparison.distanciaMetros,
        fuenteCoordenadas: comparison.fuenteCoordenadas,
        // F4.06: Datos multi-fuente del Orchestrator
        multiSourceScore: geocoding.crossValidationScore,
        multiSourceStatus: geocoding.validationStatus,
        sourcesCount: geocoding.sourcesConsulted?.length ?? 0,
        multiSourceDiscrepancyMeters: geocoding.discrepancyMeters,
        requiresManualReview: geocoding.requiresManualReview,
        reviewReason: geocoding.reviewReason,
        ...rastroTestigo,
      }
    }
  } else {
    // Sin coordenadas finales (Caso C o revisión manual)
    return {
      ...infra,
      xFinal: validDocX ?? undefined,
      yFinal: validDocY ?? undefined,
      hasCoordinates: !!(validDocX && validDocY),
      score: finalScore,
      confidence: scoreToConfidence(finalScore),
      status: finalStatus,
      source: finalSource,
      geocoding: geocoding,
      ...(catastroParcel ? { catastroParcel } : {}),
      crossValidation: {
        caso: comparison.caso,
        flag: comparison.flag,
        distanciaMetros: comparison.distanciaMetros,
        fuenteCoordenadas: comparison.fuenteCoordenadas,
        // F4.06: Datos multi-fuente del Orchestrator
        multiSourceScore: geocoding.crossValidationScore,
        multiSourceStatus: geocoding.validationStatus,
        sourcesCount: geocoding.sourcesConsulted?.length ?? 0,
        multiSourceDiscrepancyMeters: geocoding.discrepancyMeters,
        requiresManualReview: geocoding.requiresManualReview,
        reviewReason: geocoding.reviewReason,
        ...rastroTestigo,
      }
    }
  }
}

/** Lo que `geocodificaFila` necesita del orquestador (en las pruebas, un doble). */
type GeocodificadorFila = Pick<GeocodingOrchestrator, 'geocode'>

/**
 * EDAR-b (DOP-1 c): una fila de la pasada, de principio a fin: orquestador → comparador y, si la
 * fila es hidráulica, trae coordenada del plan y queda en 70, testigo G10 → comparador otra vez.
 * La usan el *worker* del *hook* y el arnés H1, para que la medida sea la de la app. Solo se
 * descarga G10 (una vez por municipio, en caché) para las filas que pueden subir.
 *
 * @internal Exportada en `_internal` para el arnés y las pruebas
 */
async function geocodificaFila(
  infra: ProcessedInfrastructure,
  metadata: DocumentMetadata,
  orchestrator: GeocodificadorFila,
  servicioG10: Pick<DERAG10R2Service, 'buscarTestigo'> = getDERAG10R2Service()
): Promise<ProcessedInfrastructure> {
  const options = toOrchestrationOptions(infra, metadata)
  const result = await orchestrator.geocode(options)
  const aplicado = applyGeocodingResult(infra, result, metadata)
  if (aplicado.score !== NOTA_SIN_CONTRASTAR || !options.codMun) return aplicado
  if (!tieneCoordenadaDocumento(infra) || !claseHidraulicaG10(infra.nombre)) return aplicado
  let testigo: TestigoG10 | null = null
  try {
    testigo = await servicioG10.buscarTestigo({
      codINE: options.codMun,
      nombre: infra.nombre || '',
      direccion: infra.direccion || infra.direccionAlt || undefined,
      municipio: options.municipality,
    })
  } catch (err) {
    // Sin testigo la fila se queda como estaba (70); se avisa para que no pase en silencio.
    console.warn(`[useAutoGeocoding] EDAR-b — testigo G10 no disponible para ${infra.nombre}:`, err)
    return aplicado
  }
  return testigo ? applyGeocodingResult(infra, result, metadata, testigo) : aplicado
}

/**
 * Calcula estadísticas de geocodificación
 * ARCH-04: Incluye estadísticas de validación cruzada
 */
function calculateStats(
  results: ProcessedInfrastructure[],
  startTime: number
): AutoGeocodingStats {
  // OBS-FN-02-A: añadir 'manual' — REVISION_MANUAL conserva las coords del
  // documento (score 30 es un aviso, no ausencia). La fila tiene xFinal/yFinal
  // finitos y se considera resuelta. Alinea con pickInfrasForDisplay.isResolved.
  const geocoded = results.filter(r => r.status === 'geocoded' || r.status === 'manual' || r.status === 'original')
  const failed = results.filter(r => r.source === 'geocoding_failed' || r.status === 'pending')
  
  // Contar uso de geocodificadores
  const geocoderUsage: Record<string, number> = {}
  for (const r of results) {
    const geocoder = r.geocoding?.geocoderUsed || r.source || 'unknown'
    geocoderUsage[geocoder] = (geocoderUsage[geocoder] || 0) + 1
  }
  
  // Score promedio
  const totalScore = results.reduce((sum, r) => sum + r.score, 0)
  const averageScore = results.length > 0 
    ? Math.round(totalScore / results.length) 
    : 0
  
  // ARCH-04: Estadísticas de validación cruzada
  const crossValidation = {
    validados: results.filter(r => r.crossValidation?.flag === 'VALIDADO').length,
    revisionMenor: results.filter(r => r.crossValidation?.flag === 'REVISION_MENOR').length,
    corregidos: results.filter(r => r.crossValidation?.flag === 'USAR_REFERENCIA').length,
    conflictos: results.filter(r => r.crossValidation?.flag === 'REVISION_MANUAL').length,
    geocodificados: results.filter(r => 
      r.crossValidation?.flag === 'GEOCODIFICADO' || 
      r.crossValidation?.flag === 'BAJA_CONFIANZA'
    ).length,
    sinCoordenadas: results.filter(r => r.crossValidation?.flag === 'SIN_COORDENADAS').length,
  }
  
  return {
    total: results.length,
    success: geocoded.length,
    failed: failed.length,
    geocoderUsage,
    averageScore,
    processingTime: Date.now() - startTime,
    crossValidation
  }
}

// ============================================================================
// HOOK
// ============================================================================

export function useAutoGeocoding(): UseAutoGeocodingResult {
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [progress, setProgress] = useState<AutoGeocodingProgress>({ 
    processed: 0, 
    total: 0, 
    percentage: 0 
  })
  const [results, setResults] = useState<ProcessedInfrastructure[]>([])
  const [parcial, setParcial] = useState<GeolocalizacionParcial | null>(null)
  const [stats, setStats] = useState<AutoGeocodingStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // Ref para evitar múltiples ejecuciones simultáneas
  const processingRef = useRef(false)

  // CAMDOC: generación de la tanda. `reset()` la incrementa; una tanda que
  // termina con una generación vieja (el documento ya se canceló o se cambió)
  // no escribe nada: ni progreso, ni resultados, ni estadísticas, ni libera
  // el candado de una tanda posterior.
  const generationRef = useRef(0)
  
  // Instancia del orquestador (se crea una vez)
  const orchestratorRef = useRef<GeocodingOrchestrator | null>(null)

  const geocodeNeeded = useCallback(async (
    infrastructures: ProcessedInfrastructure[],
    metadata: DocumentMetadata
  ): Promise<ProcessedInfrastructure[]> => {
    // Evitar ejecuciones simultáneas
    if (processingRef.current) {
      console.warn('[useAutoGeocoding] Ya hay una geocodificación en curso')
      return results
    }
    
    // ARCH-04: Filtrar todas las infraestructuras (siempre true), excepto
    // las marcadas como metadatos del documento (C4 — InputQualityFilter)
    const toGeocode = infrastructures.filter(needsGeocoding)
    const skippedMetadata = infrastructures.filter(i => !needsGeocoding(i))

    if (skippedMetadata.length > 0) {
      console.info(
        `[useAutoGeocoding] C4 — SKIP_METADATA: ${skippedMetadata.length} registro(s) omitido(s)`,
        skippedMetadata.map(i => i.nombre)
      )
    }
    
    // FILC4 (DOP-1 c): lo que no entra en la pasada sale «sin contrastar», no
    // con la nota de lectura. Se calcula una vez y se usa en los tres sitios
    // donde la fila apartada se ve: la siembra de la barra, la tabla mientras
    // se geolocaliza (`terminadas`) y el resultado final.
    const apartadas = new Map<string, ProcessedInfrastructure>(
      skippedMetadata.map(i => [i.id, marcarSinContrastar(i, metadata)])
    )

    const generation = generationRef.current
    const vigente = () => generation === generationRef.current

    if (toGeocode.length === 0) {
      console.log('[useAutoGeocoding] Sin infraestructuras para procesar')
      // FILC4: un plan con todas las filas apartadas también publica el
      // resultado, para que P1 no se quede con la nota de lectura.
      const soloApartadas = infrastructures.map(i => apartadas.get(i.id) ?? i)
      if (apartadas.size > 0 && vigente()) setResults(soloApartadas)
      return soloApartadas
    }

    processingRef.current = true
    setIsGeocoding(true)
    setError(null)
    setProgress({ processed: 0, total: toGeocode.length, percentage: 0 })
    // P1PROV-a (DOP-3 a): la tabla de P1 sabe desde el principio qué filas
    // esperan a la geolocalización; las demás salen ya con su valor.
    const enPasada: ReadonlySet<string> = new Set(toGeocode.map(i => i.id))
    // FILC4: las apartadas entran ya como «terminadas» (con su valor final):
    // si no, la tabla las tomaría de la normalización (95) hasta el final.
    setParcial({ enPasada, terminadas: new Map(apartadas) })
    
    const startTime = Date.now()

    try {
      // Inicializar orquestador si no existe
      if (!orchestratorRef.current) {
        orchestratorRef.current = new GeocodingOrchestrator()
      }
      const orchestrator = orchestratorRef.current
      
      // VALMUNI-b (DOP-1 a): el contorno del municipio, pedido ya en P1 al
      // conocer el INE (`finalizeExtraction`), se espera aquí con tope de 3 s
      // y en paralelo con los datos locales: así la primera fila ya lo tiene.
      const ineContorno = metadata.ineCode
        || (metadata.municipality ? getCodigoINE(metadata.municipality) : null)
      const contorno = precargarContorno(ineContorno)

      // C3 — Race condition fix: esperar a que LocalDataService esté listo
      // antes de lanzar la cascada de geocodificación. Si no está listo en
      // 5 s, continuamos igualmente para no bloquear al usuario.
      try {
        await Promise.race([
          loadLocalData(),
          new Promise<void>(resolve => setTimeout(resolve, 5000))
        ])
        console.info('[useAutoGeocoding] C3 — LocalDataService listo')
      } catch (localDataErr) {
        console.warn('[useAutoGeocoding] C3 — LocalDataService no disponible, continuando sin datos locales:', localDataErr)
      }
      const estadoContorno = await contorno
      console.info(`[useAutoGeocoding] VALMUNI-b — contorno ${ineContorno ?? '—'}: ${estadoContorno}`)

      // Map para resultados geocodificados (id → infra actualizada)
      const geocodedMap = new Map<string, ProcessedInfrastructure>()

      // P1-S4 (c): acumulador por vía. Se actualiza dentro del worker
      // (antes de resolver el ítem) y se publica en onItemDone, que
      // runWithConcurrency dispara tras cada resolución.
      const porVia = emptyViaBreakdown()
      // P1UNO (DOP-0 a′): siembra con las filas que no entran en la pasada
      // (placeholders, metadatos, nombres de calidad <20): su vía y su tramo
      // no cambian, y sin ellas la barra en vivo no cuadraría con la final.
      const porCalidad = emptyCalidadBreakdown()
      for (const original of infrastructures) {
        if (enPasada.has(original.id)) continue
        // FILC4: con su valor final («sin contrastar»), no con el de lectura.
        const infra = apartadas.get(original.id) ?? original
        porVia[deriveProcedencia(infra, infra.hasCoordinates)] += 1
        porCalidad[tramoCalidad(infra)] += 1
      }
      // La foto sembrada sale antes del primer ítem: el panel no cambia de
      // base al llegar el primero.
      if (vigente()) {
        setProgress({
          processed: 0,
          total: toGeocode.length,
          percentage: 0,
          porVia: { ...porVia },
          porCalidad: { ...porCalidad }
        })
      }

      // P1PROV-a: filas ya anunciadas por `onItemDone` (las que ve la tabla)
      // y su cuenta (la que ve la cabecera).
      const mapaTerminadas = new Map<string, ProcessedInfrastructure>(apartadas)
      let hechas = 0

      // R.04: concurrencia controlada con orden garantizado.
      // runWithConcurrency preserva results[i] === input[i] siempre,
      // independientemente del orden de resolución de las promesas.
      const processedInOrder = await runWithConcurrency(
        toGeocode,
        EXTERNAL_CONCURRENCY,
        async (infra) => {
          try {
            // EDAR-b: orquestador, comparador y testigo G10 en una sola función (la del arnés).
            const applied = await geocodificaFila(infra, metadata, orchestrator)
            porVia[deriveProcedencia(applied, applied.hasCoordinates)] += 1
            porCalidad[tramoCalidad(applied)] += 1
            // P1PROV-a: el resultado de la fila se guarda antes de resolver,
            // para que `onItemDone` (que solo recibe el índice) lo publique.
            geocodedMap.set(applied.id, applied)
            return applied
          } catch (err) {
            console.warn(`[useAutoGeocoding] Error geocodificando ${infra.nombre}:`, err)
            porVia.sin_geo += 1
            porCalidad.withoutCoords += 1
            const conError = {
              ...infra,
              status: 'pending' as const,
              source: 'geocoding_error'
            }
            geocodedMap.set(conError.id, conError)
            return conError
          }
        },
        (idx, total) => {
          // P1PROV-a (DOP-2 a): fila a fila, una foto nueva de las terminadas.
          // DOP-6 a: la cabecera cuenta filas terminadas, no el índice de la
          // última (con concurrencia 2 la fila 2 puede acabar antes que la 1).
          // Cuenta y tabla suben a la vez, con la misma fila.
          hechas += 1
          const fila = toGeocode[idx]
          const resultado = fila ? geocodedMap.get(fila.id) : undefined
          if (fila && resultado) mapaTerminadas.set(fila.id, resultado)
          if (!vigente()) return
          setParcial({ enPasada, terminadas: new Map(mapaTerminadas) })
          setProgress({
            processed: hechas,
            total,
            percentage: Math.round((hechas / total) * 100),
            current: toGeocode[idx]?.nombre,
            currentInfraName: toGeocode[idx]?.nombre,
            porVia: { ...porVia },
            porCalidad: { ...porCalidad }
          })
        }
      )
      
      // Guardar resultados en el mapa (orden ya garantizado por runWithConcurrency)
      for (const result of processedInOrder) {
        geocodedMap.set(result.id, result)
      }
      
      // Combinar resultados: actualizar solo las geocodificadas
      const finalResults = infrastructures.map(infra => 
        geocodedMap.get(infra.id) || apartadas.get(infra.id) || infra
      )
      
      // Calcular estadísticas de todas las procesadas
      const processedResults = Array.from(geocodedMap.values())
      const finalStats = calculateStats(processedResults, startTime)
      
      if (!vigente()) {
        console.info('[useAutoGeocoding] CAMDOC — tanda descartada: el documento se canceló o cambió mientras geocodificaba')
        return infrastructures
      }

      setResults(finalResults)
      setStats(finalStats)
      
      // ARCH-04: Log detallado de validación cruzada
      console.log(`[useAutoGeocoding] Completado:`, {
        total: finalStats.total,
        avgScore: finalStats.averageScore,
        crossValidation: finalStats.crossValidation,
        tiempo: `${finalStats.processingTime}ms`
      })
      
      return finalResults
      
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido'
      if (vigente()) setError(message)
      console.error('[useAutoGeocoding] Error:', err)
      return infrastructures
      
    } finally {
      if (vigente()) {
        setIsGeocoding(false)
        // P1PROV-a (DOP-5 a): al terminar o al fallar, los parciales se van en
        // el mismo lote en que llegan `results` (o el error): la tabla pasa
        // de la mezcla al resultado final sin un render intermedio.
        setParcial(null)
        processingRef.current = false
      }
    }
  }, [results])

  const reset = useCallback(() => {
    generationRef.current += 1
    setIsGeocoding(false)
    setProgress({ processed: 0, total: 0, percentage: 0 })
    setResults([])
    setParcial(null)
    setStats(null)
    setError(null)
    processingRef.current = false
  }, [])

  // P2-S3: actualiza campos de un resultado por id sin mutar los demás.
  // Clon del patchResult de useNormalization (BUG-7). Necesario porque la
  // cascada de display (adaptStep2Props / pickInfrasForDisplay) usa esta
  // fuente cuando no está vacía: sin él, «Confirmar elemento» sería inerte
  // en el flujo geocodificado (DOP-γ 12-jul).
  const patchResult = useCallback((id: string, patch: Partial<ProcessedInfrastructure>) => {
    setResults(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r))
  }, [])

  return {
    geocodeNeeded,
    isGeocoding,
    progress,
    results,
    parcial,
    stats,
    error,
    reset,
    patchResult
  }
}

export default useAutoGeocoding

/** @visibleForTesting — helpers internos exportados solo para tests unitarios */
export const _internal = {
  toOrchestrationOptions,
  applyGeocodingResult,
  needsGeocoding,
  scoreNameQuality,
  marcarSinContrastar,
  calculateStats,
  esCentroideMunicipal,
  confirmaConTestigoG10,
  geocodificaFila,
}
